import { db } from '@/db/races'
import type { Race } from '@/graphql/types/race'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs, type StoreDefinition } from 'pinia'
import { watch } from 'vue'

export class RaceSyncService {
  private worker: Worker
  private syncListeners: Set<(status: SyncStatus) => void> = new Set()
  private authStore?: ReturnType<typeof useAuthStore>

  constructor() {
    this.worker = new Worker(
      new URL('../workers/sync.worker.ts', import.meta.url),
      { type: 'module' }
    )
    this.worker.onmessage = this.handleWorkerMessage.bind(this)
  }

  // Initialize auth store after Pinia is ready
  initialize(store: ReturnType<typeof useAuthStore>) {
    this.authStore = store

    if(this.authStore) {
      const { token } = storeToRefs(this.authStore)

      watch(token, (token: string | null) => {
        this.worker.postMessage({
          type: 'AUTH_TOKEN',
          token: token
        })
      })

      if (this.authStore?.token) {
        this.worker.postMessage({ type: 'AUTH_TOKEN', token: this.authStore.token })
      }
    }
    return this
  }

  startBackgroundSync(intervalMinutes = 5) {
    this.worker.postMessage({ type: 'START_SYNC', intervalMinutes })
  }

  stopBackgroundSync() {
    this.worker.postMessage({ type: 'STOP_SYNC' })
  }

  async manualSync(): Promise<void> {
    this.worker.postMessage({ type: 'MANUAL_SYNC' })
  }

  async pushPerformance(resultId: number): Promise<void> {
    this.worker.postMessage({ type: 'PUSH_PERFORMANCE', resultId })
  }

  onSyncStatusChange(callback: (status: SyncStatus) => void) {
    this.syncListeners.add(callback)
    return () => this.syncListeners.delete(callback)
  }

  private async handleWorkerMessage(event: MessageEvent) {
    switch (event.data.type) {
      case 'SYNC_STARTED':
        this.notifyListeners({ status: 'syncing' })
        break

      case 'SYNC_COMPLETED':
        this.notifyListeners({ status: 'completed' })
        break

      case 'SYNC_ERROR':
        this.notifyListeners({
          status: 'error',
          error: new Error(event.data.error)
        })
        break
    }
  }

  private notifyListeners(status: SyncStatus) {
    this.syncListeners.forEach(listener => listener(status))
  }
}

export type SyncStatus = {
  status: 'syncing' | 'completed' | 'error'
  error?: Error
}

// Optional: Create a singleton instance that can be initialized later
let instance: RaceSyncService | null = null

export const getRaceSyncService = () => {
  if (!instance) {
    instance = new RaceSyncService()
  }
  return instance
}
